/**
 * Bot - um inimigo: corpo, sentidos, arma e cerebro.
 *
 * Movimento: capsula resolvida por ctx.collision.capsuleMove. O bot NAO tem
 * caminho privilegiado - ele bate na mesma parede que o jogador, sobe o mesmo
 * degrau e cai da mesma laje. Nada de "teleporte para o waypoint".
 *
 * Mira (o ponto que decide se o jogo e justo):
 *   - o bot mira num ponto ATRASADO em relacao ao jogador (constante de tempo
 *     ~0.12 s). Andar de lado engana o bot, como tem que ser.
 *   - o erro angular comeca grande e cai exponencialmente enquanto ele mantem o
 *     alvo em vista (tempo de fixacao 0.25-0.45 s), e NUNCA chega a zero.
 *   - o erro cresce com distancia, com a velocidade do alvo, com a propria
 *     velocidade e sob supressao.
 *   - o desvio nao e sorteado tiro a tiro (isso vira "acerta ou nao acerta"
 *     aleatorio); e um ruido SUAVIZADO, que faz a rajada varrer o alvo. E o que
 *     da a sensacao de estar sendo caçado por alguem, nao por um dado.
 *
 * Cerebro: maquina de estados com percepcao (cone de 110 graus + linha de visao
 * real + audicao) e memoria de ultima posicao conhecida. Regra de ouro: nunca
 * correr em linha reta ate a morte - procurar cobertura, espiar, flanquear
 * (coordenado pelo BotManager) e recuar quando ferido.
 */

import * as THREE from 'three';
import { WORLD, TEAM } from '../core/Constants.js';
import { BotModel } from './BotModel.js';

export const BOT_STATE = {
  IDLE: 'IDLE',
  PATROL: 'PATROL',
  INVESTIGATE: 'INVESTIGATE',
  CHASE: 'CHASE',
  ATTACK: 'ATTACK',
  TAKE_COVER: 'TAKE_COVER',
  FLANK: 'FLANK',
  RELOAD: 'RELOAD',
  DEAD: 'DEAD'
};

/** Tres perfis de armamento. Um pelotao com tres cadencias diferentes soa muito melhor. */
const BOT_WEAPONS = [
  {
    id: 'rifle', name: 'Fuzil', rpm: 600, damage: 15, range: 95, mag: 30, reload: 2.5,
    burstMin: 3, burstMax: 6, burstGap: 0.44, spread: 0.011,
    errStart: 0.205, errMin: 0.0365, distScale: 1.00, preferred: 17, maxEngage: 48,
    damageFalloffStart: 22, damageFalloffEnd: 80, minDamageScale: 0.55, penetration: 0.35,
    sfx: 'enemy_rifle'
  },
  {
    id: 'smg', name: 'Submetralhadora', rpm: 820, damage: 10, range: 55, mag: 32, reload: 2.1,
    burstMin: 5, burstMax: 9, burstGap: 0.34, spread: 0.020,
    errStart: 0.255, errMin: 0.0540, distScale: 1.20, preferred: 11, maxEngage: 32,
    damageFalloffStart: 12, damageFalloffEnd: 45, minDamageScale: 0.45, penetration: 0.2,
    sfx: 'enemy_smg'
  },
  {
    id: 'dmr', name: 'Fuzil de precisao', rpm: 240, damage: 25, range: 130, mag: 12, reload: 2.9,
    burstMin: 1, burstMax: 2, burstGap: 0.95, spread: 0.004,
    errStart: 0.150, errMin: 0.0220, distScale: 0.35, preferred: 27, maxEngage: 78,
    damageFalloffStart: 40, damageFalloffEnd: 120, minDamageScale: 0.7, penetration: 0.6,
    sfx: 'enemy_dmr'
  }
];

const STAND_HEIGHT = 1.80;
const CROUCH_HEIGHT = 1.22;
const BOT_RADIUS = 0.34;
const EYE_STAND = 1.62;
const EYE_CROUCH = 1.06;

const VIEW_RANGE = 72;
const VIEW_COS = Math.cos((110 * Math.PI) / 180 / 2); // meio-angulo de 55 graus
const CLOSE_SENSE = 4.5;                               // nao da para ignorar alguem colado

const SPEED_PATROL = 2.0;
const SPEED_ALERT = 3.3;
const SPEED_RUN = 4.5;
const SPEED_SPRINT = 5.4;   // abaixo do sprint do jogador (6.6): da para escapar
const SPEED_CROUCH = 1.5;

const TURN_BODY = 6.5;      // rad/s
const TURN_LOOK = 11.0;
const MAX_TORSO_TWIST = 0.95;

// --- temporarios de modulo --------------------------------------------------
const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _c = new THREE.Vector3();
const _d = new THREE.Vector3();

let _nextId = 1;

export class Bot {
  constructor(ctx, manager) {
    this.ctx = ctx;
    this.manager = manager;
    this.id = _nextId++;
    this.name = 'Inimigo';
    this.isBot = true;
    this.team = TEAM.ENEMY;

    const seed = this.id * 7919 + 13;
    this.rnd = makeRng(seed);

    // --- corpo ------------------------------------------------------------
    this.position = new THREE.Vector3();
    this.velocity = new THREE.Vector3();
    this.radius = BOT_RADIUS;
    this.grounded = false;
    this.crouching = false;
    this.eyeHeight = EYE_STAND;

    this.maxHealth = 100;
    this.health = this.maxHealth;
    this.alive = false;
    this.active = false;

    // --- perfil (varia entre bots: pelotao com gente diferente) -------------
    this.skill = 0.35 + this.rnd() * 0.5;             // 0..1
    this.weapon = BOT_WEAPONS[Math.floor(this.rnd() * BOT_WEAPONS.length)];
    this.reactionTime = 0.45 - this.skill * 0.20;     // 0.25 .. 0.45 s
    this.focusTime = 0.45 - this.skill * 0.18;
    this.trackTau = 0.17 - this.skill * 0.07;         // atraso de acompanhamento
    this.aggression = 0.3 + this.rnd() * 0.6;
    this.coverAffinity = 0.35 + this.rnd() * 0.6;

    // --- modelo -----------------------------------------------------------
    this.model = new BotModel(this.id % 3, seed);
    this.hitboxes = this.model.hitboxes;
    for (const hb of this.hitboxes) hb.object.userData.entity = this;

    // --- navegacao --------------------------------------------------------
    /** @type {THREE.Vector3[]} reaproveitado; pathLen diz quantos valem */
    this.path = [];
    this.pathLen = 0;
    this.pathIndex = 0;
    this.pathTimer = 0;
    this.destination = new THREE.Vector3();
    this.hasDestination = false;
    this.arrived = false;
    this.stuckTimer = 0;
    this.stuckRef = new THREE.Vector3();
    this.repathFails = 0;

    // --- percepcao --------------------------------------------------------
    this.canSee = false;
    this.seeTime = 0;
    this.lostTime = 99;
    this.lastKnown = new THREE.Vector3();
    this.hasLastKnown = false;
    this.noisePos = new THREE.Vector3();
    this.hasNoise = false;
    this.alerted = false;
    this.contactShouted = false;
    this.alertCooldown = 0;
    this.targetSpeed = 0;
    this._prevTargetPos = new THREE.Vector3();
    this._hasPrevTarget = false;

    // --- mira -------------------------------------------------------------
    this.aimPoint = new THREE.Vector3();
    this.aimValid = false;
    this.aimHold = 0;
    this.offX = 0; this.offY = 0;
    this.offTX = 0; this.offTY = 0;
    this.offTimer = 0;
    this.bodyYaw = 0;
    this.lookYaw = 0;
    this.lookPitch = 0;
    this.scanYaw = 0;
    this.scanTimer = 0;
    this.scanTarget = 0;

    // --- arma -------------------------------------------------------------
    this.ammo = this.weapon.mag;
    this.fireTimer = 0;
    this.burstLeft = 0;
    this.burstFired = 0;
    this.burstPause = 0;
    this.reloadTimer = 0;
    this.wantsFire = false;
    this.aiming = false;
    this._fireOrigin = new THREE.Vector3();
    this._fireDir = new THREE.Vector3();

    // --- estado -----------------------------------------------------------
    this.state = BOT_STATE.IDLE;
    this.stateTime = 0;
    this.returnState = BOT_STATE.ATTACK;
    this.suppress = 0;
    this.sinceDamage = 99;
    this.role = 'assault';
    this.flankPoint = new THREE.Vector3();
    this.hasFlankPoint = false;
    this.coverPoint = new THREE.Vector3();
    this.hasCover = false;
    this.coverTimer = 0;
    this.peeking = false;
    this.peekTimer = 0;
    this.coverSearchCooldown = 0;
    this.strafeDir = this.rnd() < 0.5 ? -1 : 1;
    this.strafeTimer = 0;
    this.separation = new THREE.Vector3();
    this.deathTime = 0;
    this.killEventSeen = false;

    // --- direcao desejada (preenchida pelo cerebro, consumida pelo movimento)
    this.moveDir = new THREE.Vector3();
    this.moveSpeed = 0;

    this._pose = {
      speed: 0, crouch: 0, aim: 0, torsoYaw: 0, pitch: 0, scanYaw: 0, reload: 0
    };

    this._coverList = [];
    this._brokenWarned = false;
  }

  // =========================================================================
  // ciclo de vida
  // =========================================================================

  addToScene(scene) {
    if (scene && this.model.root.parent !== scene) scene.add(this.model.root);
  }

  spawnAt(x, y, z) {
    this.position.set(x, y, z);
    this.velocity.set(0, 0, 0);
    this.health = this.maxHealth;
    this.alive = true;
    this.active = true;
    this.grounded = false;
    this.crouching = false;
    this.eyeHeight = EYE_STAND;

    this.ammo = this.weapon.mag;
    this.fireTimer = 0;
    this.burstLeft = 0;
    this.burstFired = 0;
    this.burstPause = 0;
    this.reloadTimer = 0;
    this.wantsFire = false;
    this.aiming = false;

    this.canSee = false;
    this.seeTime = 0;
    this.lostTime = 99;
    this.hasLastKnown = false;
    this.hasNoise = false;
    this.alerted = false;
    this.contactShouted = false;
    this.alertCooldown = 0;
    this.suppress = 0;
    this.aimHold = 0;
    this.aimValid = false;
    this._hasPrevTarget = false;

    this.pathLen = 0;
    this.pathIndex = 0;
    this.pathTimer = 0;
    this.hasDestination = false;
    this.arrived = false;
    this.stuckTimer = 0;
    this.repathFails = 0;
    this.stuckRef.copy(this.position);

    this.hasCover = false;
    this.hasFlankPoint = false;
    this.sinceDamage = 99;
    this.coverSearchCooldown = 0;
    this.role = 'assault';
    this.deathTime = 0;
    this.killEventSeen = false;

    this.bodyYaw = this.rnd() * Math.PI * 2;
    this.lookYaw = this.bodyYaw;
    this.lookPitch = 0;

    this.state = BOT_STATE.IDLE;
    this.stateTime = -(0.3 + this.rnd() * 1.6); // escalona: o pelotao nao acorda junto
    this._restChecked = false;

    this.model.resetPose();
    this.model.setVisible(true);
    this.model.root.position.copy(this.position);
    this.model.root.rotation.set(0, this.bodyYaw, 0);

    this._register();
  }

  _register() {
    const targets = this.ctx.targets;
    if (!targets || typeof targets.register !== 'function') return;
    try {
      targets.register(this, {
        hitboxes: this.hitboxes,
        team: TEAM.ENEMY,
        onDamage: this._onDamageBound || (this._onDamageBound = (a, b) => this.onDamage(a, b))
      });
      this.registered = true;
    } catch (err) {
      this._warnOnce('falha ao registrar em ctx.targets', err);
    }
  }

  _unregister() {
    if (!this.registered) return;
    this.registered = false;
    try {
      this.ctx.targets?.unregister?.(this);
    } catch (err) {
      this._warnOnce('falha ao remover de ctx.targets', err);
    }
  }

  /** Some do mundo e devolve o corpo ao pool. Limpar o registro aqui e obrigatorio. */
  despawn() {
    this._unregister();
    this.alive = false;
    this.active = false;
    this.state = BOT_STATE.DEAD;
    this.model.setVisible(false);
    this.velocity.set(0, 0, 0);
  }

  dispose() {
    this._unregister();
    this.model.dispose();
  }

  _warnOnce(msg, err) {
    if (this._brokenWarned) return;
    this._brokenWarned = true;
    console.warn(`[Bot ${this.id}] ${msg}`, err || '');
  }

  // =========================================================================
  // dano e morte
  // =========================================================================

  /**
   * Chamado pela balistica. A assinatura exata varia entre implementacoes, entao
   * aceita (valor, info) e tambem (info) num objeto so.
   */
  onDamage(amount, info) {
    let amt = amount;
    let data = info;
    if (amount && typeof amount === 'object') {
      data = amount;
      amt = data.amount !== undefined ? data.amount : (data.damage || 0);
    }
    const part = data?.part || null;
    const src = data?.source || data?.shooter || data?.from || null;
    const px = data?.point?.x, pz = data?.point?.z;
    return this.applyDamage(amt || 0, part, src, px, pz, data?.dir);
  }

  /** Mesma assinatura do Player, para quem preferir chamar assim. */
  damage(amount, from, part) {
    return this.applyDamage(amount || 0, part || null, null, from?.x, from?.z, null);
  }

  applyDamage(amount, part, source, srcX, srcZ, dir) {
    if (!this.alive || amount <= 0) return 0;
    this.health -= amount;
    this.sinceDamage = 0;
    this.suppress = Math.min(1, this.suppress + 0.45 + amount * 0.01);

    // direcao do impacto: usada para o tranco e para a queda
    let dx = 0, dz = 1;
    if (dir && Number.isFinite(dir.x)) { dx = dir.x; dz = dir.z; }
    else if (Number.isFinite(srcX)) { dx = this.position.x - srcX; dz = this.position.z - srcZ; }
    const dl = Math.hypot(dx, dz) || 1;
    dx /= dl; dz /= dl;

    // levar tiro pelas costas vira informacao: e assim que o bot descobre voce
    const shooterPos = source?.position || this.ctx.player?.position;
    if (shooterPos && !this.canSee) {
      this.lastKnown.copy(shooterPos);
      this.hasLastKnown = true;
      this.lostTime = 0;
      this.manager?.reportContact?.(this, shooterPos, false);
    }
    if (!this.alerted) {
      this.alerted = true;
      this._shout();
    }

    if (this.health <= 0) {
      this.kill(source, part === 'head', dx, dz);
      return amount;
    }

    // um tranco visivel: o jogador precisa VER que acertou
    const side = (dx * Math.cos(this.bodyYaw) - dz * Math.sin(this.bodyYaw)) >= 0 ? 1 : -1;
    this.model.addFlinch(0.10 + Math.min(0.3, amount * 0.006), side);

    // ferido: procurar cobertura em vez de trocar tiro no aberto
    if (this.state !== BOT_STATE.TAKE_COVER && this.health < this.maxHealth * (0.35 + this.coverAffinity * 0.3)) {
      this.hasCover = false;
      this._setState(BOT_STATE.TAKE_COVER);
    }
    return amount;
  }

  kill(source, headshot, dx, dz) {
    if (!this.alive) return;
    this.alive = false;
    this.health = 0;
    this.state = BOT_STATE.DEAD;
    this.deathTime = 0;
    this.wantsFire = false;
    this.velocity.set(0, 0, 0);
    this.pathLen = 0;
    // cadaver nao leva tiro: sair de ctx.targets AGORA (vazamento aqui trava o jogo)
    this._unregister();
    this.model.playDeath(
      Number.isFinite(dx) ? dx : Math.sin(this.bodyYaw),
      Number.isFinite(dz) ? dz : Math.cos(this.bodyYaw),
      this.bodyYaw,
      !!headshot
    );
    this.manager?.onBotDied?.(this, source, !!headshot);
  }

  // =========================================================================
  // percepcao
  // =========================================================================

  getAimPoint(out) {
    return out.set(this.position.x, this.position.y + 1.25, this.position.z);
  }

  getEye(out) {
    return out.set(this.position.x, this.position.y + this.eyeHeight, this.position.z);
  }

  _playerPosition() {
    const p = this.ctx.player;
    if (p && p.alive === false) return null;
    const pos = p?.position || this.ctx.controller?.position;
    if (!pos || !Number.isFinite(pos.x)) return null;
    return pos;
  }

  _perceive(dt) {
    const ppos = this._playerPosition();
    if (!ppos) {
      this.canSee = false;
      this.seeTime = 0;
      this.lostTime += dt;
      return;
    }

    // velocidade do alvo medida aqui: nao depende de o Player expor velocity
    if (this._hasPrevTarget && dt > 1e-4) {
      const vx = (ppos.x - this._prevTargetPos.x) / dt;
      const vz = (ppos.z - this._prevTargetPos.z) / dt;
      const sp = Math.hypot(vx, vz);
      this.targetSpeed += (Math.min(12, sp) - this.targetSpeed) * 0.5;
    }
    this._prevTargetPos.copy(ppos);
    this._hasPrevTarget = true;

    const eh = this.ctx.controller?.eyeHeight;
    const chestY = Number.isFinite(eh) ? eh - 0.34 : 1.28;

    this.getEye(_a);
    _b.set(ppos.x, ppos.y + chestY, ppos.z);

    const dx = _b.x - _a.x, dy = _b.y - _a.y, dz = _b.z - _a.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    let visible = false;

    if (dist < VIEW_RANGE) {
      // cone de visao: 110 graus. Colado, o cone nao vale (visao periferica).
      let inCone = dist < CLOSE_SENSE;
      if (!inCone) {
        const h = Math.hypot(dx, dz) || 1;
        const fx = Math.sin(this.lookYaw), fz = Math.cos(this.lookYaw);
        inCone = (dx / h) * fx + (dz / h) * fz > VIEW_COS;
      }
      if (inCone) {
        visible = this._lineOfSight(_a, _b);
        if (!visible && dist < 42) {
          // segundo teste na cabeca: pega jogador agachado atras de mureta
          _b.y = ppos.y + (Number.isFinite(eh) ? eh : 1.62) - 0.05;
          visible = this._lineOfSight(_a, _b);
          if (!visible) _b.y = ppos.y + chestY;
        }
      }
    }

    this.canSee = visible;
    if (visible) {
      this.seeTime += dt;
      this.lostTime = 0;
      this.lastKnown.copy(ppos);
      this.hasLastKnown = true;
      this.aimValid = true;
      this.alerted = true;
      // Contato VISUAL sempre grita e avisa o esquadrao - mesmo que ele ja
      // estivesse alerta por ter ouvido tiro. "Ja estava alerta" nao pode
      // engolir o grito: e por ele que o jogador sabe que foi visto.
      if (!this.contactShouted) {
        this.contactShouted = true;
        this._shout();
        this.manager?.reportContact?.(this, ppos, true);
      }
    } else {
      this.lostTime += dt;
      // memoria curta: perde a fixacao mas nao esquece na hora
      if (this.lostTime > 0.6) this.seeTime = Math.max(0, this.seeTime - dt * 1.4);
      if (this.lostTime > 12) this.contactShouted = false;
    }
  }

  _lineOfSight(from, to) {
    const col = this.ctx.collision;
    if (!col) return true;
    try {
      if (typeof col.lineOfSight === 'function') return !!col.lineOfSight(from, to);
      if (typeof col.raycast === 'function') {
        _c.copy(to).sub(from);
        const len = _c.length();
        if (len < 1e-4) return true;
        _c.multiplyScalar(1 / len);
        const hit = col.raycast(from, _c, len - 0.15);
        return !hit;
      }
    } catch (err) {
      this._warnOnce('lineOfSight falhou', err);
    }
    return true;
  }

  _shout() {
    if (this.alertCooldown > 0) return;
    this.alertCooldown = 7;
    this.ctx.events?.emit?.(this.ctx.EVENTS.AI_ALERT, { bot: this, position: this.position });
    this.ctx.events?.emit?.(this.ctx.EVENTS.SFX, {
      name: 'ai_alert', position: this.position, volume: 0.8, pitch: 0.92 + this.rnd() * 0.18
    });
  }

  /** Chamado pelo BotManager quando algo faz barulho (tiro, passo, grito). */
  hear(x, y, z, strength) {
    if (!this.alive) return;
    if (this.canSee) return;
    // ruido distante entra com imprecisao: o bot vai para a REGIAO, nao para o ponto
    const err = (1 - strength) * 6;
    this.noisePos.set(
      x + (this.rnd() - 0.5) * err,
      y,
      z + (this.rnd() - 0.5) * err
    );
    this.hasNoise = true;
    if (strength > 0.55 && !this.alerted) this.alerted = true;
    if (this.state === BOT_STATE.IDLE || this.state === BOT_STATE.PATROL) {
      this._setState(BOT_STATE.INVESTIGATE);
    }
  }

  // =========================================================================
  // navegacao
  // =========================================================================

  /**
   * @param {boolean} force true = pode furar o intervalo minimo entre buscas.
   *   Estado que persegue alvo movel (CHASE/FLANK) SEMPRE passa false: sem isso
   *   o bot recalcularia o A* a cada think, que e o jeito classico de derrubar
   *   o frame com 8 inimigos em cena.
   */
  _setDestination(x, y, z, force) {
    if (this.hasDestination) {
      const dx = this.destination.x - x, dz = this.destination.z - z;
      if (dx * dx + dz * dz < 1.0 && this.pathLen > 0) return; // ja indo para la
    }
    this.destination.set(x, y, z);
    this.hasDestination = true;
    this.arrived = false;
    this._repath(force === true);
  }

  _repath(force) {
    if (!this.hasDestination) return;
    if (!force && this.pathTimer > 0) return;
    // orcamento global: no maximo uma busca A* por passo fixo, entre todos os bots
    if (this.manager && this.manager.consumePathBudget && !this.manager.consumePathBudget()) {
      this.pathTimer = 0.05;
      return;
    }
    this.pathTimer = 0.55 + this.rnd() * 0.35;
    const nav = this.manager?.navmesh;
    if (!nav || !nav.ready) {
      // sem navegacao: segue em linha reta (o capsuleMove impede atravessar parede)
      this.pathLen = 0;
      return;
    }
    const n = nav.findPathInto(this.position, this.destination, this.path);
    this.pathLen = n;
    this.pathIndex = n > 1 ? 1 : 0;
    if (n === 0) {
      this.repathFails++;
      if (this.repathFails > 2) { this.hasDestination = false; this.repathFails = 0; }
    } else {
      this.repathFails = 0;
    }
  }

  /** Direcao para o proximo waypoint, ja avancando o indice quando chega perto. */
  _followPath() {
    this.moveDir.set(0, 0, 0);
    if (this.pathLen > 0) {
      while (this.pathIndex < this.pathLen) {
        const wp = this.path[this.pathIndex];
        const dx = wp.x - this.position.x;
        const dz = wp.z - this.position.z;
        const d2 = dx * dx + dz * dz;
        const last = this.pathIndex === this.pathLen - 1;
        const reach = last ? 0.65 : 0.85;
        if (d2 < reach * reach) { this.pathIndex++; continue; }
        const inv = 1 / Math.sqrt(d2);
        this.moveDir.set(dx * inv, 0, dz * inv);
        return true;
      }
      this.pathLen = 0;
      this.arrived = true;
      this.hasDestination = false;
      return false;
    }
    if (this.hasDestination) {
      // sem caminho calculado, tenta a reta
      const dx = this.destination.x - this.position.x;
      const dz = this.destination.z - this.position.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < 0.8) { this.arrived = true; this.hasDestination = false; return false; }
      const inv = 1 / Math.sqrt(d2);
      this.moveDir.set(dx * inv, 0, dz * inv);
      return true;
    }
    return false;
  }

  _distanceToPlayerSq() {
    const p = this._playerPosition();
    if (!p) return Infinity;
    const dx = p.x - this.position.x, dz = p.z - this.position.z;
    return dx * dx + dz * dz;
  }

  // =========================================================================
  // cerebro: chamado pelo BotManager em rodizio (~12-20 Hz por bot)
  // =========================================================================

  think(dt) {
    if (!this.alive) return;
    this.stateTime += dt;
    this.alertCooldown = Math.max(0, this.alertCooldown - dt);
    this.pathTimer = Math.max(0, this.pathTimer - dt);
    this.coverSearchCooldown = Math.max(0, this.coverSearchCooldown - dt);
    this.suppress = Math.max(0, this.suppress - dt * 0.55);
    this.sinceDamage += dt;

    this._perceive(dt);
    this._updateSeparation();
    this._checkStuck(dt);

    // Regenera depois de 6 s sem levar tiro (e nao "fora de combate"): e o que
    // fecha o ciclo ferido -> cobertura -> recupera -> volta a pressionar. Sem
    // isso o bot ferido acampa na cobertura ate o fim da partida.
    if (this.sinceDamage > 6 && this.health < this.maxHealth) {
      this.health = Math.min(this.maxHealth, this.health + dt * 7);
    }

    // pente vazio interrompe qualquer estado: recarregar vem antes de tudo
    if (this.ammo <= 0 && this.state !== BOT_STATE.RELOAD && this.state !== BOT_STATE.DEAD) {
      this.returnState = this.state;
      this._setState(BOT_STATE.RELOAD);
    }

    switch (this.state) {
      case BOT_STATE.IDLE: this._stIdle(dt); break;
      case BOT_STATE.PATROL: this._stPatrol(dt); break;
      case BOT_STATE.INVESTIGATE: this._stInvestigate(dt); break;
      case BOT_STATE.CHASE: this._stChase(dt); break;
      case BOT_STATE.ATTACK: this._stAttack(dt); break;
      case BOT_STATE.TAKE_COVER: this._stTakeCover(dt); break;
      case BOT_STATE.FLANK: this._stFlank(dt); break;
      case BOT_STATE.RELOAD: this._stReload(dt); break;
      default: break;
    }

    this._updateScan(dt);
  }

  _setState(next) {
    if (this.state === next) return;
    this.state = next;
    this.stateTime = 0;
    this.arrived = false;
    this.burstLeft = 0; // rajada nao atravessa troca de estado
    this.burstFired = 0;
    if (next !== BOT_STATE.ATTACK) this.wantsFire = false;
    if (next === BOT_STATE.IDLE) this._restChecked = false;
    if (next === BOT_STATE.TAKE_COVER) { this.peeking = false; this.peekTimer = 0.5 + this.rnd() * 0.8; }
    if (next === BOT_STATE.RELOAD) {
      this.reloadTimer = this.weapon.reload * (0.92 + this.rnd() * 0.2);
      this.ctx.events?.emit?.(this.ctx.EVENTS.SFX, {
        name: 'enemy_reload', position: this.position, volume: 0.5, pitch: 1
      });
    }
  }

  /** Comeca combate: escolhe entre atacar, flanquear ou buscar cobertura. */
  _engage() {
    if (this.role === 'flank' && this.hasFlankPoint) { this._setState(BOT_STATE.FLANK); return; }
    const d2 = this._distanceToPlayerSq();
    const maxE = this.weapon.maxEngage;
    if (this.canSee && d2 < maxE * maxE) this._setState(BOT_STATE.ATTACK);
    else this._setState(BOT_STATE.CHASE);
  }

  _stIdle(dt) {
    this.crouching = false;
    if (this.canSee) { this._engage(); return; }
    if (this.hasNoise) { this._setState(BOT_STATE.INVESTIGATE); return; }

    // Parar no eixo de uma rua aberta e o que faz o inimigo parecer manequim em
    // qualquer print: em jogo comercial o inimigo ou esta em cobertura ou esta
    // em movimento com peso. Ao cair em IDLE no aberto ele anda os poucos metros
    // ate o abrigo mais proximo e SO ENTAO para.
    if (this._seekRest(dt)) return;

    this.moveSpeed = 0;
    if (this.stateTime > 2.0 + this.rnd() * 3) this._setState(BOT_STATE.PATROL);
  }

  /**
   * Anda ate o posto de espera. Devolve true enquanto estiver a caminho.
   *
   * Uma tentativa por parada (`_restChecked`): sem isso, o bot que esta no meio
   * de uma praca sem nada por perto refaria a varredura a cada think.
   */
  _seekRest(dt) {
    if (!this.hasDestination) {
      if (this._restChecked) return false;
      this._restChecked = true;
      const nav = this.manager?.navmesh;
      if (!nav || !nav.ready) return false;
      // ja encostado em parede, container ou veiculo: nao precisa procurar
      if (nav.exposureAt(this.position.x, this.position.z) >= 3) return false;
      const p = nav.restPointNear(this.position, 8, _d);
      if (!p) return false;
      this._setDestination(p.x, p.y, p.z, true);
    }
    if (this._followPath()) {
      this.moveSpeed = SPEED_PATROL * 0.8;
      this._faceMovement(dt);
      return true;
    }
    this.hasDestination = false;
    return false;
  }

  _stPatrol(dt) {
    this.crouching = false;
    if (this.canSee) { this._engage(); return; }
    if (this.hasNoise) { this._setState(BOT_STATE.INVESTIGATE); return; }

    if (!this.hasDestination || this.arrived) {
      const nav = this.manager?.navmesh;
      let p = nav?.randomPoint(this.position, 20, _d);
      // fim de trecho com abrigo por perto: assim a patrulha TERMINA encostada
      // em alguma coisa, em vez de terminar no meio da via
      if (p) {
        const rest = nav.restPointNear(p, 5, _c);
        if (rest) p = rest;
        this._setDestination(p.x, p.y, p.z, true);
      } else {
        this._setState(BOT_STATE.IDLE);
      }
      // parada curta entre trechos: patrulha sem pausa parece esteira rolante
      if (this.rnd() < 0.25) { this._setState(BOT_STATE.IDLE); return; }
    }
    this._followPath();
    this.moveSpeed = SPEED_PATROL;
    this._faceMovement(dt);
  }

  _stInvestigate(dt) {
    this.crouching = false;
    if (this.canSee) { this._engage(); return; }

    if (this.hasNoise) {
      this._setDestination(this.noisePos.x, this.noisePos.y, this.noisePos.z, true);
      this.hasNoise = false;
    } else if (!this.hasDestination && this.hasLastKnown && this.stateTime < 1) {
      this._setDestination(this.lastKnown.x, this.lastKnown.y, this.lastKnown.z, true);
    }

    if (this.hasDestination && this._followPath()) {
      this.moveSpeed = SPEED_ALERT;
      this._faceMovement(dt);
    } else {
      this.moveSpeed = 0;
      // varre o local antes de desistir
      if (this.stateTime > 3.5 + this.rnd() * 3) {
        this.alerted = false;
        this.hasLastKnown = false;
        this._setState(BOT_STATE.PATROL);
      }
    }
  }

  _stChase(dt) {
    this.crouching = false;
    const d2 = this._distanceToPlayerSq();
    const maxE = this.weapon.maxEngage;

    if (this.canSee && this.seeTime > 0.12 && d2 < maxE * maxE) {
      this._setState(BOT_STATE.ATTACK);
      return;
    }
    if (!this.hasLastKnown) { this._setState(BOT_STATE.PATROL); return; }

    // persegue a ULTIMA POSICAO CONHECIDA, nao a posicao real (nao ha onisciencia)
    const tgt = this.canSee ? this._playerPosition() : this.lastKnown;
    if (tgt) this._setDestination(tgt.x, tgt.y, tgt.z, false);

    if (this._followPath()) {
      // corre, mas nao em linha reta ate a morte: se ja esta perto, anda alerta
      this.moveSpeed = d2 > 400 ? SPEED_SPRINT : SPEED_RUN;
      this._faceMovement(dt);
      this.aiming = d2 < 900;
    } else {
      this.moveSpeed = 0;
      this.hasNoise = false;
      if (this.stateTime > 1.5) this._setState(BOT_STATE.INVESTIGATE);
    }

    if (this.lostTime > 12) {
      this.alerted = false;
      this._setState(BOT_STATE.INVESTIGATE);
    }
  }

  _stAttack(dt) {
    // sem visao, mira a ULTIMA POSICAO CONHECIDA: bot que gira o corpo seguindo
    // o jogador atraves da parede e o tipo de trapaça que o jogador percebe na hora
    const real = this._playerPosition();
    const ppos = (this.canSee || !this.hasLastKnown) ? real : this.lastKnown;
    if (!ppos) { this._setState(BOT_STATE.PATROL); return; }

    const d2 = this._distanceToPlayerSq();
    const dist = Math.sqrt(d2);
    const w = this.weapon;

    // perdeu de vista: nao fica atirando em parede
    if (!this.canSee && this.lostTime > 1.0) { this._setState(BOT_STATE.CHASE); return; }

    // ferido ou sob pressao: cobertura
    const hurt = this.health < this.maxHealth * 0.45;
    if ((hurt || this.suppress > 0.75) && this.coverSearchCooldown <= 0 && this.rnd() < 0.8) {
      this.hasCover = false;
      this._setState(BOT_STATE.TAKE_COVER);
      return;
    }
    if (this.role === 'flank' && this.hasFlankPoint && dist > 12) {
      this._setState(BOT_STATE.FLANK);
      return;
    }

    this.aiming = true;
    // quem esta na funcao de supressao, longe, se abaixa: fica menor e mais preciso
    this.crouching = (this.role === 'suppress' && dist > 18);

    // posicionamento: manter a distancia preferida da arma e nao ficar parado
    const wantClose = w.preferred * (this.role === 'assault' ? 0.8 : 1.25);
    let radial = 0;
    if (dist > wantClose + 5) radial = 1;
    else if (dist < wantClose * 0.55) radial = -1;

    this.strafeTimer -= dt;
    if (this.strafeTimer <= 0) {
      this.strafeTimer = 1.1 + this.rnd() * 1.9;
      this.strafeDir = this.rnd() < 0.5 ? -1 : 1;
    }

    // direcao radial + tangencial, direto (sem A*): sao passos curtos
    const dx = ppos.x - this.position.x;
    const dz = ppos.z - this.position.z;
    const inv = 1 / Math.max(0.001, Math.hypot(dx, dz));
    const rx = dx * inv, rz = dz * inv;
    const tx = -rz * this.strafeDir, tz = rx * this.strafeDir;

    const strafeAmount = this.canSee ? 0.85 : 0.25;
    this.moveDir.set(rx * radial + tx * strafeAmount, 0, rz * radial + tz * strafeAmount);
    const ml = this.moveDir.length();
    if (ml > 0.01) {
      this.moveDir.multiplyScalar(1 / ml);
      this.moveSpeed = radial > 0 ? SPEED_RUN : SPEED_ALERT * 0.9;
      this.hasDestination = false;
      this.pathLen = 0;
    } else {
      this.moveSpeed = 0;
    }

    // atirar: so depois do tempo de reacao, e so com o alvo realmente visivel
    this.wantsFire = this.canSee && this.seeTime >= this.reactionTime && dist < w.range;
    this._faceTarget(dt, ppos);
  }

  _stTakeCover(dt) {
    const real = this._playerPosition();
    const ppos = (this.canSee || !this.hasLastKnown) ? (real || this.lastKnown) : this.lastKnown;

    if (!this.hasCover && this.coverSearchCooldown > 0) {
      // ainda no intervalo entre buscas: recua da ameaca em vez de congelar
      if (ppos) {
        const rx = this.position.x - ppos.x;
        const rz = this.position.z - ppos.z;
        const inv = 1 / Math.max(0.001, Math.hypot(rx, rz));
        this.moveDir.set(rx * inv, 0, rz * inv);
        this.moveSpeed = SPEED_ALERT;
        this.hasDestination = false;
        this.pathLen = 0;
        this._faceTarget(dt, ppos);
      }
      this.wantsFire = false;
      return;
    }

    if (!this.hasCover && this.coverSearchCooldown <= 0) {
      this.coverSearchCooldown = 1.6 + this.rnd();
      const nav = this.manager?.navmesh;
      const list = nav?.coverPointsNear(this.position, ppos, 13, this._coverList);
      if (list && list.length > 0) {
        // nao correr todos para o mesmo buraco: o manager reserva o ponto
        const chosen = this.manager?.claimCover?.(this, list) || list[0];
        this.coverPoint.copy(chosen);
        this.hasCover = true;
        this._setDestination(chosen.x, chosen.y, chosen.z, true);
      } else {
        // Sem cobertura por perto: volta a atacar recuando. Espera bem mais
        // antes de tentar de novo - com a busca curta o bot ficava piscando
        // entre ATTACK e TAKE_COVER a cada 1.6 s em terreno aberto.
        this.coverSearchCooldown = 4.5 + this.rnd() * 2.5;
        this._setState(BOT_STATE.ATTACK);
        return;
      }
    }

    const dxc = this.coverPoint.x - this.position.x;
    const dzc = this.coverPoint.z - this.position.z;
    const atCover = this.hasCover && (dxc * dxc + dzc * dzc) < 1.4;

    if (!atCover) {
      // o caminho pode ter sido descartado por outro estado (recarga, recuo):
      // reemite o destino em vez de ficar parado esperando o tempo esgotar
      if (this.hasCover && !this.hasDestination) {
        this._setDestination(this.coverPoint.x, this.coverPoint.y, this.coverPoint.z, false);
      }
      if (this._followPath()) {
        this.moveSpeed = SPEED_RUN;
        this.crouching = false;
        this.aiming = false;
        this._faceMovement(dt);
      } else {
        this.moveSpeed = 0;
        if (this.stateTime > 4) { this.hasCover = false; this._setState(BOT_STATE.ATTACK); }
      }
      this.wantsFire = false;
      return;
    }

    // chegou na cobertura: ciclo de esconder / espiar
    this.moveSpeed = 0;
    this.aiming = true;

    // recarregar escondido, e nao no meio do tiroteio: e o uso obvio da
    // cobertura e evita o bot espiar com tres balas no pente
    if (!this.peeking && this.ammo < this.weapon.mag * 0.45) {
      this.returnState = BOT_STATE.TAKE_COVER;
      this._setState(BOT_STATE.RELOAD);
      return;
    }

    this.peekTimer -= dt;
    if (this.peekTimer <= 0) {
      this.peeking = !this.peeking;
      this.peekTimer = this.peeking
        ? (0.8 + this.rnd() * 1.1)                            // tempo exposto: curto
        : (0.9 + this.rnd() * 1.5) * (1.35 - this.aggression); // escondido: nem tanto,
      // bot que fica 3 s sem revidar parece que desligou
    }
    this.crouching = !this.peeking;
    this.wantsFire = this.peeking && this.canSee && this.seeTime >= this.reactionTime * 0.7;
    if (ppos) this._faceTarget(dt, ppos);

    // recuperou / jogador chegou perto / cobertura virou armadilha: volta a atacar
    const d2 = this._distanceToPlayerSq();
    if (d2 < 49) { this._setState(BOT_STATE.ATTACK); return; }
    if (this.health > this.maxHealth * 0.65 && this.stateTime > 4 && this.suppress < 0.2) {
      this._setState(BOT_STATE.ATTACK);
      return;
    }
    if (this.lostTime > 9) { this._setState(BOT_STATE.CHASE); }
  }

  _stFlank(dt) {
    if (!this.hasFlankPoint) { this._setState(BOT_STATE.ATTACK); return; }
    this.crouching = false;

    this._setDestination(this.flankPoint.x, this.flankPoint.y, this.flankPoint.z, false);
    const d2 = this._distanceToPlayerSq();

    if (this._followPath()) {
      this.moveSpeed = SPEED_SPRINT;
      this.aiming = false;
      this._faceMovement(dt);
    } else {
      this.moveSpeed = 0;
      this.hasFlankPoint = false;
      this._setState(BOT_STATE.ATTACK);
      return;
    }

    // durante o flanco so atira se o alvo aparecer bem na frente
    this.wantsFire = false;
    if (this.canSee && d2 < 225 && this.seeTime > this.reactionTime) {
      this.hasFlankPoint = false;
      this._setState(BOT_STATE.ATTACK);
    }
    if (this.stateTime > 11) { this.hasFlankPoint = false; this._setState(BOT_STATE.ATTACK); }
  }

  _stReload(dt) {
    this.wantsFire = false;
    this.reloadTimer -= dt;
    this.aiming = false;

    // recarregar no aberto e morte: recua ou vai para cobertura enquanto isso
    const ppos = this._playerPosition();
    if (ppos && this.canSee) {
      const dx = this.position.x - ppos.x;
      const dz = this.position.z - ppos.z;
      const inv = 1 / Math.max(0.001, Math.hypot(dx, dz));
      this.moveDir.set(dx * inv, 0, dz * inv);
      this.moveSpeed = SPEED_ALERT;
      this.hasDestination = false;
      this.pathLen = 0;
      this._faceTarget(dt, ppos);
    } else if (this.hasCover) {
      if (this._followPath()) { this.moveSpeed = SPEED_RUN; this._faceMovement(dt); }
      else this.moveSpeed = 0;
    } else {
      this.moveSpeed = 0;
    }

    if (this.reloadTimer <= 0) {
      this.ammo = this.weapon.mag;
      this.burstLeft = 0;
      this.burstPause = 0.2;
      const back = (this.returnState && this.returnState !== BOT_STATE.RELOAD) ? this.returnState : BOT_STATE.ATTACK;
      this._setState(this.canSee ? BOT_STATE.ATTACK : back);
    }
  }

  /** Cabeca varrendo o ambiente quando nao ha alvo. Bot de olhar fixo parece morto. */
  _updateScan(dt) {
    if (this.canSee) {
      this.scanYaw += (0 - this.scanYaw) * Math.min(1, dt * 6);
      return;
    }
    this.scanTimer -= dt;
    if (this.scanTimer <= 0) {
      this.scanTimer = 1.4 + this.rnd() * 2.6;
      this.scanTarget = (this.rnd() - 0.5) * 1.3;
    }
    this.scanYaw += (this.scanTarget - this.scanYaw) * Math.min(1, dt * 1.6);
  }

  _updateSeparation() {
    this.separation.set(0, 0, 0);
    const bots = this.manager?.bots;
    if (!bots) return;
    for (let i = 0; i < bots.length; i++) {
      const o = bots[i];
      if (o === this || !o.alive) continue;
      const dx = this.position.x - o.position.x;
      const dz = this.position.z - o.position.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > 1.9 || d2 < 1e-4) continue;
      const dist = Math.sqrt(d2);
      const w = (1.38 - dist) * 1.4; // quanto mais colado, mais forte o empurrao
      this.separation.x += (dx / dist) * w;
      this.separation.z += (dz / dist) * w;
    }
  }

  _checkStuck(dt) {
    if (this.moveSpeed < 0.2) { this.stuckTimer = 0; this.stuckRef.copy(this.position); return; }
    this.stuckTimer += dt;
    if (this.stuckTimer < 0.9) return;
    const dx = this.position.x - this.stuckRef.x;
    const dz = this.position.z - this.stuckRef.z;
    this.stuckTimer = 0;
    this.stuckRef.copy(this.position);
    if (dx * dx + dz * dz > 0.16) return;

    // travado: refaz o caminho e, se insistir, escolhe outro destino
    this.repathFails++;
    if (this.repathFails > 2) {
      this.repathFails = 0;
      const nav = this.manager?.navmesh;
      const p = nav?.randomPoint(this.position, 9, _d);
      if (p) this._setDestination(p.x, p.y, p.z, true);
      else this.hasDestination = false;
      this.strafeDir = -this.strafeDir;
    } else {
      this._repath(true);
    }
  }

  // =========================================================================
  // orientacao
  // =========================================================================

  _faceMovement(dt) {
    if (this.moveDir.lengthSq() < 1e-4) return;
    const want = Math.atan2(this.moveDir.x, this.moveDir.z);
    this.bodyYaw = turnToward(this.bodyYaw, want, TURN_BODY * dt);
    // olhando um pouco a frente do proprio movimento
    this.lookYaw = turnToward(this.lookYaw, want + this.scanYaw, TURN_LOOK * dt);
    this.lookPitch += (0 - this.lookPitch) * Math.min(1, dt * 5);
  }

  _faceTarget(dt, ppos) {
    const dx = ppos.x - this.position.x;
    const dz = ppos.z - this.position.z;
    const dy = (ppos.y + 1.25) - (this.position.y + this.eyeHeight);
    const horiz = Math.hypot(dx, dz);
    const want = Math.atan2(dx, dz);
    const wantPitch = Math.atan2(dy, Math.max(0.2, horiz));

    this.lookYaw = turnToward(this.lookYaw, want, TURN_LOOK * dt);
    this.lookPitch += (wantPitch - this.lookPitch) * Math.min(1, dt * 8);

    // o corpo so acompanha o que o torso nao consegue torcer
    const twist = shortAngle(this.lookYaw - this.bodyYaw);
    if (Math.abs(twist) > MAX_TORSO_TWIST * 0.6 || this.moveSpeed > 0.5) {
      this.bodyYaw = turnToward(this.bodyYaw, this.lookYaw, TURN_BODY * dt);
    }
  }

  // =========================================================================
  // passo fixo: movimento e tiro (roda todo fixedUpdate, e barato)
  // =========================================================================

  step(h) {
    if (!this.active) return;
    if (!this.alive) { this.deathTime += h; return; }

    this._move(h);
    this._updateAim(h);
    this._fireStep(h);

    // altura do olho acompanha o agachamento com suavidade
    const wantEye = this.crouching ? EYE_CROUCH : EYE_STAND;
    this.eyeHeight += (wantEye - this.eyeHeight) * Math.min(1, h * 9);
  }

  _move(h) {
    const dirLen = this.moveDir.lengthSq();
    let wantX = 0, wantZ = 0;
    if (dirLen > 1e-4 && this.moveSpeed > 0.01) {
      const inv = 1 / Math.sqrt(dirLen);
      wantX = this.moveDir.x * inv;
      wantZ = this.moveDir.z * inv;
    }

    // separacao dos companheiros: entra como desvio, nao como destino
    wantX += this.separation.x * 0.55;
    wantZ += this.separation.z * 0.55;
    const wl = Math.hypot(wantX, wantZ);
    if (wl > 1) { wantX /= wl; wantZ /= wl; }

    let speed = this.moveSpeed;
    if (this.crouching) speed = Math.min(speed, SPEED_CROUCH);

    const targetVx = wantX * speed;
    const targetVz = wantZ * speed;
    const accel = this.grounded ? 14 : 2.2;
    const k = 1 - Math.exp(-accel * h);
    this.velocity.x += (targetVx - this.velocity.x) * k;
    this.velocity.z += (targetVz - this.velocity.z) * k;
    this.velocity.y -= WORLD.GRAVITY * h;
    if (this.velocity.y < -55) this.velocity.y = -55;

    const height = this.crouching ? CROUCH_HEIGHT : STAND_HEIGHT;
    const col = this.ctx.collision;
    let moved = false;
    if (col && typeof col.capsuleMove === 'function') {
      try {
        const res = col.capsuleMove(this.position, this.velocity, h, this.radius, height);
        if (res && res.position) {
          this.position.copy(res.position);
          if (res.velocity) this.velocity.copy(res.velocity);
          this.grounded = !!res.grounded;
          moved = true;
        }
      } catch (err) {
        this._warnOnce('capsuleMove falhou', err);
      }
    }
    if (!moved) {
      // degradacao: integra na mao e cola na altura da navegacao
      this.position.x += this.velocity.x * h;
      this.position.z += this.velocity.z * h;
      this.position.y += this.velocity.y * h;
      const gy = this.manager?.navmesh?.heightAt(this.position.x, this.position.z);
      if (Number.isFinite(gy)) {
        if (this.position.y < gy) { this.position.y = gy; this.velocity.y = 0; this.grounded = true; }
        else this.grounded = false;
      }
    }
    if (this.grounded && this.velocity.y < 0) this.velocity.y = -1.5;

    // barulho de passo: o jogador ouve o inimigo se aproximar
    const hs = Math.hypot(this.velocity.x, this.velocity.z);
    this._stepPhase = (this._stepPhase || 0) + hs * h;
    if (this.grounded && this._stepPhase > 1.55) {
      this._stepPhase = 0;
      this.ctx.events?.emit?.(this.ctx.EVENTS.FOOTSTEP, {
        position: this.position, surface: 'concrete', isPlayer: false, running: hs > 3.6
      });
    }
  }

  /**
   * Mira humana. Duas camadas:
   *  - aimPoint persegue o alvo COM ATRASO (trackTau): quem troca de direcao
   *    sai da mira.
   *  - erro angular com ruido suavizado, que encolhe com o tempo de fixacao.
   */
  _updateAim(h) {
    // sem visao a mira persegue a ultima posicao conhecida, nao a real: o bot
    // nao pode estar pre-apontado no ponto exato quando o jogador reaparece
    const real = this._playerPosition();
    const ppos = this.canSee ? real : (this.hasLastKnown ? this.lastKnown : real);
    if (!ppos) { this.aimValid = false; this.aimHold = 0; return; }

    const eh = this.ctx.controller?.eyeHeight;
    const chestY = Number.isFinite(eh) ? eh - 0.34 : 1.28;
    _a.set(ppos.x, ppos.y + chestY, ppos.z);

    if (!this.aimValid) { this.aimPoint.copy(_a); this.aimValid = true; }
    else {
      const k = 1 - Math.exp(-h / Math.max(0.03, this.trackTau));
      this.aimPoint.lerp(_a, k);
    }

    if (this.canSee) this.aimHold += h;
    else this.aimHold = Math.max(0, this.aimHold - h * 2.2);

    // ruido de mira: alvo novo a cada ~0.15 s, interpolado -> varredura, nao pulo
    this.offTimer -= h;
    if (this.offTimer <= 0) {
      this.offTimer = 0.11 + this.rnd() * 0.14;
      this.offTX = gauss(this.rnd);
      this.offTY = gauss(this.rnd) * 0.7;
    }
    const ko = Math.min(1, h * 10);
    this.offX += (this.offTX - this.offX) * ko;
    this.offY += (this.offTY - this.offY) * ko;
  }

  _currentAimError(dist) {
    const w = this.weapon;
    const decay = Math.exp(-this.aimHold / Math.max(0.08, this.focusTime * 0.6));
    let err = w.errMin + (w.errStart - w.errMin) * decay;
    // Crescimento superlinear com a distancia: de longe o bot e so incomodo,
    // de perto e ameaca real. Linear puro o deixa preciso demais a 35 m.
    // distScale da identidade a cada arma: a submetralhadora despenca com a
    // distancia, o fuzil de precisao quase nao sente.
    err *= 1 + (dist * 0.013 + dist * dist * 0.00018) * w.distScale;
    err *= 1 + this.targetSpeed * 0.075;
    err *= 1 + Math.hypot(this.velocity.x, this.velocity.z) * 0.08;
    err *= 1 + this.suppress * 0.85;
    // subida de coice DENTRO da rajada: o primeiro tiro e o preciso, os
    // seguintes sobem. Faz a rajada "andar" pela parede em vez de empilhar
    // furos no mesmo ponto - e da ao jogador uma janela real de reacao.
    err *= 1 + this.burstFired * 0.16;
    if (this.crouching) err *= 0.84;
    // piso duro: perfeicao nunca. Teto: nem tanto que vire palhacada.
    if (err < w.errMin * 0.85) err = w.errMin * 0.85;
    if (err > 0.32) err = 0.32;
    return err;
  }

  _fireStep(h) {
    if (this.burstPause > 0) this.burstPause -= h;
    if (this.fireTimer > 0) this.fireTimer -= h;
    if (!this.wantsFire || !this.alive) return;
    if (this.ammo <= 0) return;
    if (this.burstPause > 0 || this.fireTimer > 0) return;

    if (this.burstLeft <= 0) {
      const w = this.weapon;
      this.burstLeft = w.burstMin + Math.floor(this.rnd() * (w.burstMax - w.burstMin + 1));
      this.burstFired = 0;
    }

    this._shoot();

    this.burstFired++;
    this.ammo--;
    this.burstLeft--;
    this.fireTimer = 60 / this.weapon.rpm;
    if (this.burstLeft <= 0) {
      // pausa entre rajadas: e o que da ao jogador a janela para responder
      this.burstPause = this.weapon.burstGap * (0.7 + this.rnd() * 0.8) * (1.4 - this.aggression * 0.5);
    }
    if (this.ammo <= 0) {
      this.returnState = this.state;
      this._setState(BOT_STATE.RELOAD);
    }
  }

  _shoot() {
    const w = this.weapon;
    const origin = this._fireOrigin;
    const dir = this._fireDir;

    this.model.getMuzzleWorld(origin);
    if (!Number.isFinite(origin.x)) {
      this.getEye(origin);
    }

    const dx = this.aimPoint.x - origin.x;
    const dy = this.aimPoint.y - origin.y;
    const dz = this.aimPoint.z - origin.z;
    const horiz = Math.hypot(dx, dz);
    const dist = Math.hypot(horiz, dy);
    const err = this._currentAimError(dist);

    const yaw = Math.atan2(dx, dz) + this.offX * err;
    const pitch = Math.atan2(dy, Math.max(0.05, horiz)) + this.offY * err;
    const cp = Math.cos(pitch);
    dir.set(Math.sin(yaw) * cp, Math.sin(pitch), Math.cos(yaw) * cp);

    try {
      this.ctx.ballistics?.fire?.({
        origin,
        dir,
        weapon: w,
        shooter: this,
        team: TEAM.ENEMY,
        spread: w.spread,
        damage: w.damage,
        range: w.range,
        pellets: 1
      });
    } catch (err2) {
      this._warnOnce('ballistics.fire falhou', err2);
    }

    this.model.addRecoil(0.16 + this.rnd() * 0.06);

    try {
      this.ctx.fx?.muzzleFlash?.({ position: origin, direction: dir, scale: 0.7 });
    } catch (err3) { /* efeito e opcional, nunca derruba o frame */ }

    const ev = this.ctx.EVENTS;
    this.ctx.events?.emit?.(ev.AI_FIRE, { bot: this, origin, dir });
    this.ctx.events?.emit?.(ev.SFX, {
      name: w.sfx, position: origin, volume: 0.85, pitch: 0.95 + this.rnd() * 0.1
    });
  }

  // =========================================================================
  // visual
  // =========================================================================

  updateVisual(dt) {
    const m = this.model;
    m.root.position.copy(this.position);
    if (!m.dead) m.root.rotation.set(0, this.bodyYaw, 0);

    const p = this._pose;
    p.speed = Math.hypot(this.velocity.x, this.velocity.z);
    p.crouch = this.crouching ? 1 : 0;
    p.aim = (this.aiming || this.wantsFire) ? 1 : 0;
    p.torsoYaw = clampAbs(shortAngle(this.lookYaw - this.bodyYaw), MAX_TORSO_TWIST);
    p.pitch = this.lookPitch;
    p.scanYaw = this.canSee ? 0 : this.scanYaw * 0.5;
    if (this.state === BOT_STATE.RELOAD && this.weapon.reload > 0) {
      const prog = 1 - Math.max(0, this.reloadTimer) / this.weapon.reload;
      p.reload = prog < 0 ? 0 : (prog > 1 ? 1 : prog);
    } else {
      p.reload = 0;
    }

    m.update(dt, p);
  }
}

// ---------------------------------------------------------------------------
// utilitarios
// ---------------------------------------------------------------------------

function makeRng(seed) {
  let a = seed >>> 0;
  return function rnd() {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Aproximacao de gaussiana por soma de uniformes. Media 0, desvio ~1. */
function gauss(rnd) {
  return (rnd() + rnd() + rnd() - 1.5) * 1.15;
}

function shortAngle(a) {
  let x = a;
  while (x > Math.PI) x -= Math.PI * 2;
  while (x < -Math.PI) x += Math.PI * 2;
  return x;
}

function turnToward(current, target, maxStep) {
  const diff = shortAngle(target - current);
  if (Math.abs(diff) <= maxStep) return shortAngle(target);
  return shortAngle(current + Math.sign(diff) * maxStep);
}

function clampAbs(v, m) { return v > m ? m : (v < -m ? -m : v); }

export { BOT_WEAPONS };
